import React from 'react';
import Dropdown from '../../../frame-pc/components/dropdown';
import QuickSetting from './index';
import { IReactionDisposer, autorun, observable, reaction } from 'mobx';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { browser } from '../../../../slide/infrastructure/module/independent/agent/common';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '../../../../slide/infrastructure/module/dependent/report/offline-data-report/constant';
import _has from 'lodash/has';
import _get from 'lodash/get';
import { NOTE_CONTAINER_ID } from '@com-pc/note-area-wrapper/constant';
import { CANVAS_WRAPPER } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { PPTEditorStateType } from '../../../../slide/domain/ui-model/constant/edit-state-type';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import ChartQuickSetting from './chart-quick-setting';
import AttachmentToolbar, { isShowPreviewToolbar } from '../attachment-toolbar';
import { isUiModelIsAttachment } from '@src/slide/domain/ui-model/elements/element-judge';
import { getAttachmentShowType } from '@src/slide/domain/ui-model/element-modeles/property/attachment/utils/attachment';
import { RULER_LENGTH } from '@editor/src/js/constants';
import { DEFAULT_QUICK_SETTING_RECT } from './quick-setting-rect/model/base';
import { getQuickSettingRect } from './quick-setting-rect';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { IQuickSettingManager } from '@src/framework/frame/components/quick-setting/quick-setting-manager.interface';

const QUICK_SETTING_CLASSNAME = 'index-quickBar';
const MIN_SPACE = browser.isMobile ? 22 : 36;

/**
 * 快捷工具栏管理
 */
export default class QuickSettingManager implements IQuickSettingManager {
    private static instance: QuickSettingManager;
    public static getInstance() {
        if (!QuickSettingManager.instance) {
            QuickSettingManager.instance = new QuickSettingManager();
        }
        return QuickSettingManager.instance;
    }

    /**
     * 快捷栏是否展示
     */
    @observable public isQuickSettingShown = false;
    private quickSettingOp: ReturnType<typeof Dropdown.create>|null;
    private first: boolean;
    private showTimer: NodeJS.Timeout|null;
    private menuDisposer: IReactionDisposer|null;

    public constructor() {
        this.quickSettingOp = null;
        this.first = false;
    }

    public initWithStore = () => {
        if (!browser.isMobile) {
            this.registerListener();
        }
    };

    public hide = () => {
        this.clearShowTimer();
        if (this.quickSettingOp) {
            this.quickSettingOp.close();
            this.quickSettingOp = null;
        }
    };

    public updateAnchorRect = () => {
        if (this.quickSettingOp?.isShown()) {
            this.quickSettingOp.updateAnchorRect(this.getQuickSettingRect());
        }
    };

    public show = (selectArr?) => {
        if (this.checkStateChange()) return;
        const model = this.getSelectionService().getSelectedModel();
        const isChart = model?.type === UIModelType.CHART;
        if (isChart && !(model as IChartModel).isActualRenderChart) return;
        const selectArray = selectArr || this.getSelectionService().selectedElements.selectedArray;
        const dropContent = this.getDropContent(selectArray);
        if (!this.quickSettingOp) {
            const isMultiModel = this.isMultiModel();
            const rotate = this.getRotate();
            const dropDownProps = this.getDropDownProps(model);
            this.quickSettingOp = Dropdown.create({
                ...dropDownProps,
                anchorRect: this.getQuickSettingRect(),
                autoClose: false,
                hasShadow: false,
                scrollable: false,
                hasTransform: false,
                isTransparent: true,
                offsetY: this.getOffsetY(isMultiModel, rotate),
                ignoreBottomOffsetY: true,
                zIndex: 4,
                autoOffsetY: this.isNeedAutoOffsetY(isMultiModel) ? -16 : 0,
                containerId: CANVAS_WRAPPER,
                autoOffsetMode: 1,
                onVisibleChange: (visible) => {
                    this.handleVisibleChange(visible);
                },
                dropContent,
                autoCustomLimitOffsetY: this.getAppService().editableServiceLayer?.textRulerManager.displayRuler
                    ? RULER_LENGTH
                    : 0,
                centerAlignByWidth: true,
            });
        } else {
            const isShown = this.quickSettingOp.isShown();
            if (isShown) {
                return;
            }
        }
        this.quickSettingOp.show();
    };

    public updateContent = () => {
        const selectArray = this.getSelectionService().selectedElements.selectedArray;
        const dropContent = this.getDropContent(selectArray);
        this.quickSettingOp?.updateDropContent(dropContent);
    };

    private checkAuth = () => this.getUserPriviledgeService().pptEditorState === PPTEditorStateType.EDITABLE;

    private registerListener = () => {
        reaction(
            () => this.getTextEditorStateService().isEditorTextExpanded,
            (expand) => {
                this.handleEditExpand(expand);
            },
        );
        reaction(
            () => this.getTextEditorStateService().isEditorTextSelecting,
            (selecting) => {
                this.handleEditSelect(selecting);
            },
        );
        reaction(
            () => this.getTextEditorStateService().isEditorTextMouseDown,
            (down) => {
                this.handleEditMouseDown(down);
            },
        );

        reaction(
            () => this.checkElementChanged(),
            () => {
                this.handleElementChange();
            },
        );
        reaction(
            // eslint-disable-next-line max-len
            () => (this.checkStateChange()),
            (changing) => {
                this.handleElementStateChange(changing);
            },
        );

        reaction(
            () => this.getAttachmentManager()?.attachmentSuccessHappen,
            () => {
                this.handleAttachmentSuccess();
            },
        );

        reaction(() => this.getUserPriviledgeService().pptEditorState, () => {
            this.handleElementChange();
        });
    };

    private handleAttachmentSuccess() {
        const model = this.getSelectionService().getSelectedModel();
        if (model?.type === UIModelType.SHAPE && _get(model, 'props.attachment.objKey')) {
            this.handleElementChange();
        }
    }

    private handleElementStateChange(changing) {
        if (!this.checkAuth()) return;
        if (changing) {
            this.hide();
        } else {
            this.hide();
            this.changeSelectQuickState();
        }
    }

    private checkStateChange() {
        return this.getAppService().viewableServiceLayer.editingStateManager.isControlValuesChanging
            || this.getAppService().viewableServiceLayer.editingStateManager.isMovingWithKeyUpDown
            || this.getMenuManager()?.isColorPicking
            || this.getAppService().viewableServiceLayer.editingStateManager.isInAnyEditingState()
            || this.getAppService().viewableServiceLayer.getEditorAnimPreviewManager()?.isAnimPreviewing
            || this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarAniming;
    }

    private async handleElementChange() {
        if (this.getAppService().viewableServiceLayer.editingStateManager.isMovingWithKeyUpDown) return;
        if (this.getAppService().viewableServiceLayer.editingStateManager.isCroping) return;
        const module = await this.getAppService().editableServiceLayer?.copyPasteImageService?.promise();
        if (!module) return;
        const { ImageEditManager } = module;
        ImageEditManager.getInstance().clipDataWaiting = false;
        this.hide();
        if (!this.checkAuth() && !this.isAttachmentSelected()) return;
        this.changeSelectQuickState(true);
    }

    private checkElementChanged() {
        const selElements = this.getSelectionService().selectedElements;
        const len = selElements.length;
        let key = '';
        let attachmentShowType;
        if (len === 1) {
            const [element] = selElements.selectedArray || [];
            key = element.key;
            // 附件展示类型
            attachmentShowType = getAttachmentShowType(element as IShapeModel);
        }
        return `${len}|${key}|${attachmentShowType}`;
    }

    private handleEditMouseDown(down) {
        if (!this.checkAuth()) return;
        if (!down && !this.isShown()) {
            this.changeSelectQuickState();
        }
    }

    private handleEditSelect(selecting) {
        if (!this.checkAuth()) return;
        if (selecting) {
            this.hide();
        } else if (!this.getTextEditorStateService().isEditorTextExpanded) {
            // 看看有没有元素选中
            this.changeSelectQuickState();
        }
    }

    private handleEditExpand(expand) {
        this.hide();
        if (!this.checkAuth()) return;
        if (expand) {
            this.showTimer = setTimeout(() => {
                this.show();
                // 上报
                tdw({
                    opername: TDWOperationType.DOC_PPT,
                    module: TDWModuleType.PC_EDIT_FORMAT,
                    action: TDWActionType.FONT_POP_UP,
                });
            }, 100);
        } else {
            // 看看有没有元素选中
            if (!this.getTextEditorStateService().isEditorTextMouseDown) {
                this.changeSelectQuickState();
            }
        }
    }

    private changeSelectQuickState = (report?) => {
        const array = this.getSelectionService().selectedElements.selectedArray;
        if (array && array.length >= 1) {
            this.showTimer = setTimeout(() => {
                if (!this.getTextEditorStateService().isEditorTextMouseDown && !this.checkStateChange()) {
                    this.show(array);

                    if (report) {  // 上报
                        this.reportAction();
                    }
                }
            }, 250);
        }
    };

    private reportAction() {
        let action;
        const model = this.getSelectionService().getSelectedModel();
        if (model?.type === UIModelType.SHAPE) {
            if ((model as IShapeModel).isTextOrPlaceholder) {
                action = TDWActionType.TEXT_POP_UP;
            } else {
                action = TDWActionType.SHAPE_POP_UP;
            }
        } else if (model?.type === UIModelType.IMAGE) {
            action = TDWActionType.PIC_POP_UP;
        } else if (model?.type === UIModelType.TABLE) {
            action = TDWActionType.EXL_POP_UP;
        }
        if (action) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_EDIT_FORMAT,
                action,
            });
        }
    }

    private isMultiModel = () => {
        const array = this.getSelectionService().selectedElements.selectedArray;
        return !!(array && array.length > 1);
    };

    private clearShowTimer = () => {
        if (this.showTimer) {
            clearTimeout(this.showTimer);
            this.showTimer = null;
        }
    };

    private getQuickSettingRect = () => {
        const model = this.getSelectionService().getSelectedModel();
        const scale = this.getAppService().viewableServiceLayer.editAreaCanvasSizeManager.getCanvasScale();
        if (!model) {
            return DEFAULT_QUICK_SETTING_RECT;
        }
        const isEditorTextExpanded = this.getTextEditorStateService()?.isEditorTextExpanded;
        let rect = getQuickSettingRect(model, scale, isEditorTextExpanded);
        if (!rect) {
            rect = DEFAULT_QUICK_SETTING_RECT;
        } else {
            const c = document.getElementById(CANVAS_WRAPPER);
            if (c) {
                if (this.isCanvasAndSelected() && model.type !== UIModelType.CHART) {
                    const { currentTranslateX, currentTranslateY } = this.getPcScrollService();
                    // 定位时减去可能存在的滚动
                    return { ...rect,
                        y: rect.y - (c.children?.[0]?.scrollTop || 0) + currentTranslateY,
                        x: rect.x - (c.children?.[0]?.scrollLeft || 0) + currentTranslateX,
                    };
                }
                const bound = c.getBoundingClientRect();
                rect.x = rect.x - bound.left;
                rect.y = rect.y - bound.top;
                const noteArea = document.getElementById(NOTE_CONTAINER_ID);
                const noteHeight = (noteArea?.getBoundingClientRect().height) || 0;
                if (rect.y > bound.height - noteHeight) {
                    // 这里是为了元素低于备注时，直接隐藏掉快捷工具栏
                    rect.y = -999999;
                }
            }
        }
        return rect;
    };

    private isSelectTable = () => {
        const mode = this.getSelectionService().getSelectedModel();
        if (mode) {
            return mode.type === UIModelType.TABLE;
        }
        return false;
    };

    private getRotate = () => {
        const mode = this.getSelectionService().getSelectedModel();
        if (mode && _get(mode, 'props.style.rotate')) {
            return (mode as IElementBaseModel).props.style.rotate;
        }
        return 0;
    };

    private handleVisibleChange = (visible) => {
        if (visible) {
            requestAnimationFrame(() => {
                this.resizeFromQuickSetting(visible);
            });

            QuickSettingManager.getInstance().isQuickSettingShown = true;
        } else {
            QuickSettingManager.getInstance().isQuickSettingShown = false;
            this.resizeFromQuickSetting(visible);
        }
    };

    private getDropDownProps = (model) => {
        if (isShowPreviewToolbar(model)) {
            return {
                autoAlign: true,
            };
        }
        return {
            autoAlign: true,
            autoAlignPriority: 'top',
        };
    };

    private resizeFromQuickSetting(visible) {
        const dom = document.querySelector(`[class|=${QUICK_SETTING_CLASSNAME}]`);
        if (dom) {
            const rectTop = dom.getBoundingClientRect().top;
            const canvas = document.getElementById('canvas');
            const canvasRectTop = canvas?.getBoundingClientRect().top || 0;
            if (visible && rectTop < canvasRectTop) {
                window.dispatchEvent(new Event('resize')); // 主动触发window resize
            }
            if (!visible && (rectTop - canvasRectTop) < MIN_SPACE) {
                window.dispatchEvent(new Event('resize')); // 主动触发window resize
            }
        }
    }

    private getDropContent(selectArr) {
        const onlySelectedOne = _get(selectArr, 'length') === 1;
        if (onlySelectedOne) {
            const [selectedModel] = selectArr;
            if (selectedModel.type === UIModelType.CHART) {
                return <ChartQuickSetting
                    menuManager={this.getMenuManager()}
                    selectionService={this.getSelectionService()}
                    userPriviledgeService={this.getUserPriviledgeService()}
                />;
            }

            if (isUiModelIsAttachment(selectedModel)) {
                return <AttachmentToolbar
                    selectedModel={selectedModel}
                    pptEditorState={this.getUserPriviledgeService().pptEditorState}
                />;
            }
        }

        return <QuickSetting
            userPriviledgeService={this.getUserPriviledgeService()}
        />;
    }

    private isNeedAutoOffsetY(isMultiModel) {
        return (this.getTextEditorStateService().isEditorTextExpanded
            || this.isSelectTable() || isMultiModel) ? 0 : 1;
    }

    private getOffsetY(isMultiModel, rotate) {
        // eslint-disable-next-line no-nested-ternary
        return isMultiModel ? -8 : ((rotate && rotate > 265 && rotate < 358) ? -20 : 0);
    }

    private isShown = () => {
        if (this.quickSettingOp) {
            return this.quickSettingOp.isShown();
        }
        return false;
    };

    private isAttachmentSelected = () => {
        const selectArr = this.getSelectionService()?.selectedElements?.selectedArray;
        return selectArr && selectArr.length === 1 && isUiModelIsAttachment(selectArr[0]);
    };

    private isCanvasAndSelected = () => this.getTextEditorStateService().isEditorTextExpanded;

    private getSelectionService() {
        return this.getAppService().viewableServiceLayer?.selectionService;
    }

    private getTextEditorStateService() {
        return this.getAppService().viewableServiceLayer.textEditorStateService;
    }

    private getUserPriviledgeService() {
        return this.getAppService().viewableServiceLayer.userPriviledgeService;
    }

    private getAppService() {
        return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    }

    private getAttachmentManager() {
        return this.getAppService().viewableServiceLayer?.attachmentManager;
    }

    private getMenuManager() {
        return this.getAppService().editableServiceLayer?.menuManager;
    }

    private getPcScrollService() {
        return this.getAppService().viewableServiceLayer.pcScrollService;
    }
}

